import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import VideoPlayer from './VideoPlayer';
import './ImageLightbox.css';

const ZOOM_SCALE = 2;
const LENS_SIZE = 240;

const ImageLightbox = ({ images, index, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(index);
  const [lens, setLens] = useState({ visible: false, x: 0, y: 0, bgX: 0, bgY: 0, imgW: 0, imgH: 0 });
  const imageRef = useRef(null);

  const current = images[currentIndex];
  const showNav = images.length > 1;

  const goPrev = useCallback(() => {
    setCurrentIndex(i => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex(i => (i + 1) % images.length);
  }, [images.length]);

  // Reset lens when navigating
  useEffect(() => {
    setLens(l => ({ ...l, visible: false }));
  }, [currentIndex]);

  // Lock body scroll while open
  useBodyScrollLock(true);

  // Close on Escape, navigate on arrow keys
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, goPrev, goNext]);

  const handleMouseMove = (e) => {
    const rect = imageRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    const lensX = Math.max(0, Math.min(cx - LENS_SIZE / 2, rect.width - LENS_SIZE));
    const lensY = Math.max(0, Math.min(cy - LENS_SIZE / 2, rect.height - LENS_SIZE));

    // Clamp background so the zoomed image never scrolls beyond its own edges,
    // while still allowing the image's corners/edges to appear in the lens.
    const bgX = Math.min(0, Math.max(LENS_SIZE - rect.width * ZOOM_SCALE, LENS_SIZE / 2 - cx * ZOOM_SCALE));
    const bgY = Math.min(0, Math.max(LENS_SIZE - rect.height * ZOOM_SCALE, LENS_SIZE / 2 - cy * ZOOM_SCALE));

    setLens({
      visible: true,
      x: lensX,
      y: lensY,
      bgX,
      bgY,
      imgW: rect.width * ZOOM_SCALE,
      imgH: rect.height * ZOOM_SCALE,
    });
  };

  return createPortal(
    <div className="lightbox-overlay" onClick={onClose}>
      {showNav && (
        <button
          className="lightbox-nav lightbox-nav--prev"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous image"
        >
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
            <path d="M9 1L1 8L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
        {current.mediaType === 'video' ? (
          <div className="lightbox-image-wrapper lightbox-image-wrapper--video">
            <VideoPlayer src={current.src} loop />
          </div>
        ) : (
          <div
            className="lightbox-image-wrapper"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setLens(l => ({ ...l, visible: false }))}
          >
            <img
              ref={imageRef}
              className="lightbox-image"
              src={current.src}
              alt={current.alt}
            />
            {lens.visible && (
              <div
                className="lightbox-lens"
                style={{
                  left: lens.x,
                  top: lens.y,
                  backgroundImage: `url(${current.src})`,
                  backgroundSize: `${lens.imgW}px ${lens.imgH}px`,
                  backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
                }}
              />
            )}
          </div>
        )}
        {current.caption && <p className="lightbox-caption">{current.caption}</p>}
        {images.length > 1 && (
          <p className="lightbox-counter">{currentIndex + 1}/{images.length}</p>
        )}
      </div>

      <button className="lightbox-close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close image">
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {showNav && (
        <button
          className="lightbox-nav lightbox-nav--next"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next image"
        >
          <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
            <path d="M1 1L9 8L1 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>,
    document.body
  );
};

export default ImageLightbox;
